import $ from 'jquery'
import invariant from 'ts-invariant'
import { isMediaActive } from './viewport'
import {
  fadeIn,
  fadeOutAndRemove,
  getDocumentOuterHeight,
  getElementOffset,
  getElementOuterHeight,
  getElementOuterWidth,
  setElementPosition,
} from './domutils'

const debug = require('debug')('pm:tooltips.ts')

export type TooltipType = 'default' | 'error'
export type TooltipSize = 'm' | 'l'
export type TooltipPosition = 'right' | 'top'

export interface Options {
  title?: string | null
  position?: TooltipPosition
  type?: TooltipType
  size?: TooltipSize
}

interface Tooltip {
  close(): void
}

const DEFAULT_SELECTOR = '.js-tooltip'
const TOOLTIP_DISABLED_SELECTOR = '.js-tooltip-disabled'
const TOOLTIP_CLASSNAME = 'tooltip'

// Help tooltips usually look like a question mark in a circle.
// TODO: use `js-` prefix
const HELP_TOOLTIP_SELECTOR = '.help-tooltip'

// There can only be one tooltip on the page at a time. Let's track it here.
let currentTooltip: Tooltip | null = null

const defaults: Options = {
  title: null,
  position: 'right',
  size: 'm',
  type: 'default',
}
export function mountTooltips(selector: string, options: Options = defaults) {
  invariant(
    typeof selector === 'string' && selector.trim().length > 0,
    'invalid selector'
  )

  const settings = { ...defaults, ...options }

  enableTooltipsOnMouseInteractions()
  enableTogglingHelpTooltipsOnTouchDevices()

  function enableTooltipsOnMouseInteractions() {
    $(document).on('mouseenter', selector, event => {
      debug('event: %s', event.type)
      // Use `currentTarget` (the `.js-tooltip` element the handler is delegated
      // to) rather than `target` (the deepest hovered node). The tooltip text
      // lives in `data-tooltip` on the `.js-tooltip` element, so hovering a
      // child element (e.g. an icon) would otherwise read an empty tooltip.
      const element = event.currentTarget
      invariant(element instanceof HTMLElement)

      if (isDisabled(element)) return

      showTooltip(element)
    })

    $(document).on('mouseleave', selector, event => {
      debug('event: %s', event.type)

      hideCurrentTooltip()
    })
  }

  function enableTogglingHelpTooltipsOnTouchDevices() {
    document.addEventListener('touchend', event => {
      debug('event: %s', event.type)

      if (event.target instanceof HTMLElement) {
        // `closest`, not `matches`: this listener is on `document`, so `target` is
        // the deepest tapped node. Tapping an icon inside a `.js-tooltip` would
        // otherwise miss the match and hide the tooltip instead of toggling it —
        // the touch counterpart of the `currentTarget` read on the hover path.
        const element = event.target.closest<HTMLElement>(selector)

        if (element && isHelpTooltip(element)) {
          if (currentTooltip) {
            hideCurrentTooltip()
          } else {
            showTooltip(element)
          }
        } else {
          hideCurrentTooltip()
        }
      }
    })
  }

  function showTooltip(target: HTMLElement) {
    debug('showTooltip')

    hideCurrentTooltip()

    currentTooltip = createTooltip(
      target,
      getOptionsForTarget(target, settings)
    )
  }
}

function createTooltip(target: HTMLElement, options: Options): Tooltip {
  const tooltipElement = createTooltipElement(options)
  let delayTimer: number | null = null

  revealTooltipMomentarily()

  return {
    close: () => {
      debug('close')

      stopDelayTimer()

      fadeOutAndRemove(tooltipElement)
    },
  }

  function revealTooltipMomentarily() {
    invariant(delayTimer === null)

    delayTimer = window.setTimeout(() => {
      setTooltipPosition()
      fadeIn(tooltipElement)
      delayTimer = null
    }, 100)
  }

  function stopDelayTimer() {
    if (delayTimer) {
      window.clearTimeout(delayTimer)
      delayTimer = null
    }
  }

  function setTooltipPosition() {
    debug('setTooltipPosition. options: %o', options)

    const targetOffset = getElementOffset(target)

    let elOffsetTop = targetOffset.top
    const elOffsetLeft = targetOffset.left

    const elWidth = getElementOuterWidth(target)

    const tooltipHeight = getElementOuterHeight(tooltipElement)
    const tooltipWidth = getElementOuterWidth(tooltipElement)
    const documentHeight = getDocumentOuterHeight() || 0

    // Bottom-edge detection
    if (elOffsetTop + tooltipHeight > documentHeight) {
      elOffsetTop = documentHeight - tooltipHeight - 10
    }

    let newLeft: number | null = null
    let newTop: number | null = null
    if (isMediaActive('m')) {
      if (options.position === 'top') {
        newTop = elOffsetTop - tooltipHeight - 5
        newLeft = elOffsetLeft - tooltipWidth / 2 + elWidth / 2
      } else {
        newTop = elOffsetTop
        newLeft = elOffsetLeft + elWidth + 10
      }
    } else {
      newTop = elOffsetTop - tooltipHeight - 10
    }

    setElementPosition(tooltipElement, newTop, newLeft)
  }
}

function hideCurrentTooltip() {
  debug('hideCurrentTooltip')

  if (currentTooltip) currentTooltip.close()

  currentTooltip = null
}

function isHelpTooltip(target: HTMLElement) {
  return target.matches(HELP_TOOLTIP_SELECTOR)
}

function isDisabled(target: HTMLElement) {
  return target.matches(TOOLTIP_DISABLED_SELECTOR)
}

function parsePosition(s: string | undefined): TooltipPosition | undefined {
  switch (s) {
    case 'top':
    case 'right':
    case undefined:
      return s
    default:
      invariant(false, `Unknown position: ${s}`)
  }
}
function parseSize(s: string | undefined): TooltipSize | undefined {
  switch (s) {
    case 'l':
    case 'm':
    case undefined:
      return s
    default:
      invariant(false, `Unknown size: ${s}`)
  }
}

function parseType(s: string | undefined): TooltipType | undefined {
  switch (s) {
    case 'default':
    case 'error':
    case undefined:
      return s
    default:
      invariant(false, `Unknown type: ${s}`)
  }
}

function getOptionsForTarget(target: HTMLElement, defaults: Options): Options {
  return {
    title: target.dataset.tooltip || defaults.title,
    type: parseType(target.dataset.tooltipType) || defaults.type,
    position:
      parsePosition(target.dataset.tooltipPosition) || defaults.position,
    size: parseSize(target.dataset.tooltipSize) || defaults.size,
  }
}

function getClassNamesForTooltipElement(options: Options): string[] {
  const extra = [
    options.type === 'default' ? false : options.type,
    options.size === 'm' ? false : options.size,
    options.position,
  ]
    .filter(s => !!s)
    .map(s => `${TOOLTIP_CLASSNAME}--${s}`)

  return [TOOLTIP_CLASSNAME, ...extra]
}

function createTooltipElement(options: Options): HTMLElement {
  debug('createTooltipElement: options: %o', options)

  const classNames = getClassNamesForTooltipElement(options)

  debug('classes names: %o', classNames)

  const result = document.createElement('div')
  result.classList.add(...classNames)
  result.style.display = 'none'

  // Are we risking XSS here?
  // Most likely not: https://cheatsheetseries.owasp.org/cheatsheets/DOM_based_XSS_Prevention_Cheat_Sheet.html#rule-7-fixing-dom-cross-site-scripting-vulnerabilities
  //
  // According to https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/innerText this should also allow us to
  // use line breaks in the tooltips:
  //   As a getter, it approximates the text the user would get if they highlighted the contents of the element
  //   with the cursor and then copied it to the clipboard. As a setter this will replace the element's children
  //   with the given value, converting any line breaks into <br> elements.
  result.innerText = options.title || ''

  document.body.append(result)

  return result
}

mountTooltips(DEFAULT_SELECTOR)
